/**
* Nom : login.js
* Description : Ce fichier contient la définition de la page login 
*               Cette page fourni les champs pour permettre à l'utilisateur de s'identifier
* Date de création : 23 septembre 2024
*/

/* CSS et assets */
import { Boxibrain_logo } from 'package_ui_ihm/dist'

/* Bibliothèques et composants */
import { InputForm } from 'package_ui_ihm/dist'
import { PasswordForm } from 'package_ui_ihm/dist'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs';
import 'dayjs/locale/fr'; // <-- importe la locale
import { jwtDecode } from 'jwt-decode';

/* Hook */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useData } from 'package_ui_ihm/dist';
import { useScreenWidth } from 'package_ui_ihm/dist';


// définit 'fr' comme locale globale
dayjs.locale('fr'); 
var isToday = require("dayjs/plugin/isToday");    
dayjs.extend(isToday)


/**
* Login : affiche une page avec les champs pour permettre à l'utilisateur de s'identifier
*         le code de cette page vérifie que l'utilisateur existe, que son role lui permet d'accéder 
*         à l'application et déclenche la double authentification si nécessaire
*         Les informations qui sont stockées dans des variables liées à un useContex sont enregistrées à cette étape
*
* Type : page 
*
* Parametres : 
*  None
*/
function Login() {

    // Define useState hooks
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [usernameError, setUsernameError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [userStatus, setUserStatus] = useState(null);
    const [userClientInfo, setUserClientInfo] = useState(null);
    const [doubleAuthNeeded, setDoubleAuthNeeded] = useState(null);


    // Define useContext hooks
    const { authStatus, authToken, currentUserId, currentUser, login, getCurrentUser, confirmLogin, logout } = useAuth();
    const { path, setApiPath, setPreviousUrl, setApplicationName } = useApi();
    const { setMonths, setValidationStatus, setTargetTypes } = useData();

    const navigate = useNavigate();
  

    // Define useScreenWidth hooks
    const screenWidth = useScreenWidth();


    // Define functions
    async function loginUser(username, password) {
        logout()
        const response = await fetch(path + "/api/token/", 
            {
              method: 'POST',
              credentials: 'include',
              mode: 'cors',
              headers: {
                  accept: 'application/json',
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify({"username": username, "password": password})
            }
        );
        const data = await response.json()
        const status = response["status"]
        const token = data['access']
        const refresh = data['refresh']
        var userid
        if(token){
        userid = jwtDecode(token)["user_id"]
        } else {
        userid = null
        }
        login(status, token, refresh, userid);
        setUserStatus(null)
    }

    async function retrieveUser() {
        // Token
        const token = "Bearer " + authToken
        // details of current User
        const response = await fetch(path + "/api/au-erus/" + currentUserId + "/", 
            {
              method: 'GET',
              credentials: 'include',
              mode: 'cors',
              headers: {
                  accept: 'application/json',
                  Authorization: token,
              }
            }
          );
        const data = await response.json()
        const status = response["status"]
        const first_name = data['first_name']
        const last_name = data['last_name']
        const username = data['username']
        const role = data['role']['name']
        const is_active = Number(data['is_active'])
        const must_change_password = Number(data['must_change_password'])
        const date_last_password = data['date_last_password']
        const company = data['company']
        getCurrentUser(first_name, last_name, username, role, is_active, must_change_password, date_last_password, company)

        const client_ip = data['client_ip']
        const last_client_ip = data['last_client_ip']
        const last_login = data['last_login']
        const user_client_info = {
            "client_ip": client_ip,
            "last_client_ip": last_client_ip,
            "last_login": last_login, 
        }
        setUserClientInfo(user_client_info)

        setUserStatus(status)
    }

    async function retrieveData() {
        // Token
        const token = "Bearer " + authToken
        // Headers
        const headers = {
        accept: 'application/json',
        Authorization: token,
        }
        // Requests in parallel to win time 
        const [resMonth, resValidationStatus, resTargetType] = await Promise.all([
            // Months
            fetch(path + "/api/bo-moth/", {
                method: 'GET',
                credentials: 'include',
                mode: 'cors',
                headers,
            }),
            // Validation status
            fetch(path + "/api/bo-vast/", {
                method: 'GET',
                credentials: 'include',
                mode: 'cors',
                headers,
            }),
            // Target types
            fetch(path + "/api/bo-taty/", {
                method: 'GET',
                credentials: 'include',
                mode: 'cors',
                headers,
            }),
        ])
        // Responses in parallel to win time
        const [dataMonth, dataValidationStatus, dataTargetType] = await Promise.all([
            resMonth.json(),
            resValidationStatus.json(),
            resTargetType.json(),
        ])
        // Set context variables
        setMonths(dataMonth["results"])
        setValidationStatus(dataValidationStatus["results"])
        setTargetTypes(dataTargetType["results"])
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        if(username === null || username === ''){
            setUsernameError(true)
        }
        else
        {   
            setUsernameError(false)
        }
        if(password === null|| password === ''){
            setPasswordError(true)
        }
        else
        {   
            setPasswordError(false)
        }
        if(usernameError === false && passwordError === false){
            loginUser(username, password)
        }
    };


    // Define useEffect hooks
    useEffect(() => {
        logout()
        setApplicationName("BOXIBRAIN")
        setUserClientInfo(null)
        setDoubleAuthNeeded(null)
        /* Path for API */
        const apiUrl = process.env.REACT_APP_API_URL;
        setApiPath(apiUrl) 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(authStatus === 200 && userStatus === null) {
            retrieveUser()
        }
        setDoubleAuthNeeded(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStatus]);

    useEffect(() => {
        if(userClientInfo !== null){
            if(currentUser["role_name"] === "AT_ADMIN" || currentUser["role_name"] === "AT_RESEARCH" || currentUser["role_name"] === "AT_SUPPORT")
            {
                if(userClientInfo['last_login'] !== null && dayjs(userClientInfo['last_login']).isToday() === true)
                {
                    if(userClientInfo['last_client_ip'] !== userClientInfo['client_ip'])
                    {
                        setDoubleAuthNeeded(true)
                    }
                    else
                    {
                        setDoubleAuthNeeded(false)
                    }
                }
                else
                {
                    setDoubleAuthNeeded(true)
                }                
            }
            else
            {
                setDoubleAuthNeeded(false)
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userClientInfo]);

    useEffect(() => {
        if(authStatus === 200 && doubleAuthNeeded !== null){
            if(currentUser["role_name"] !== "AT_ADMIN" && currentUser["role_name"] !== "AT_RESEARCH" && currentUser["role_name"] !== "AT_SUPPORT")
            {
                confirmLogin(false)
                setUserStatus(null)
                setUserClientInfo(null)
                setDoubleAuthNeeded(null)
                navigate('/')
            }
            if(userStatus === 200 && currentUser["is_active"] === 1 && doubleAuthNeeded === true) 
            {
                confirmLogin(false)
                navigate('/double-authent')
            } 
            else if(userStatus === 200 && currentUser["is_active"] === 1 && doubleAuthNeeded === false)
            {
                confirmLogin(true)
                if(currentUser["role_name"] === "AT_ADMIN" || currentUser["role_name"] === "AT_RESEARCH") 
                {
                    retrieveData()
                    if(currentUser["must_change_password"] === 1)
                    {
                        setPreviousUrl('/')
                        navigate('/password-edit')
                    } else {
                        navigate('/menu')
                    }
                }
                else if(currentUser["role_name"] === "AT_SUPPORT") 
                {
                    if(currentUser["must_change_password"] === 1)
                    {
                        setPreviousUrl('/')
                        navigate('/password-edit')
                    } else {
                        navigate('/equipments')
                    }
                } 
                else 
                {
                    navigate('/')
                }
            }
            else
            {
                confirmLogin(false)
                setUserStatus(null)
                setUserClientInfo(null)
                setDoubleAuthNeeded(null)
                navigate('/')
            }  
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doubleAuthNeeded]);


    // Render component
    return (
        <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
            <form onSubmit={handleSubmit} className='flex-column form-center'>
                <div className='flex-row'>
                    <img src={Boxibrain_logo} alt='Boxibrain logo' className={(screenWidth > 650) ? 'image-login' : 'image-login-ss'}/>
                </div>
                {/*<img src={Boxibrain_logo} alt='Boxibrain logo' className={(screenWidth > 650) ? 'image-login' : 'image-login-ss'}/>*/}
                <h1 className='zero-marge-padding'>Connexion</h1>
                {(authStatus !== null && authStatus !== 200 && usernameError === false && passwordError === false) ? (
                    <p className='form-error'>Les identifiants ne correspondent pas à un compte existant !</p>
                ) : (
                    (currentUser && currentUser && currentUser["role_name"] !== "AT_ADMIN" && currentUser["role_name"] !== "AT_RESEARCH" && currentUser["role_name"] !== "AT_SUPPORT") ? (
                        <p className='form-error'>Vous n'êtes pas autorisé à accéder à cette application.</p>
                    ) : (
                        null
                    )
                )}   
                <InputForm error={usernameError} type="text" label="Identifiant" value={username} setValue={setUsername} disabled={false}/>
                <PasswordForm error={passwordError} label="Mot de passe" value={password} setValue={setPassword} disabled={false}/>
                <button type="submit" className='button'>Connexion</button>
            </form> 
            <footer className="footer">
                <p className='center-text'>©{new Date().getFullYear()} Asclepios Tech SAS. Tout droits réservés. <Link to={'/policy'} className='link-text'>Politique de confidentialité</Link></p>
            </footer>
        </div>
    );
}

export default Login;

